/**
 * ShareButtons — botões de compartilhamento social para a view de Notícia.
 *
 * URL efetiva: prop `url` se fornecida; senão `window.location.href`, lida
 * dentro do onClick (nunca no render) para ser SSR-safe.
 */
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import cx from 'clsx';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import facebookSVG from '../../../icons/facebook.svg';
import xTwitterSVG from '../../../icons/x-twitter.svg';
import whatsappSVG from '../../../icons/whatsapp.svg';
import linkedinSVG from '../../../icons/linkedin.svg';
import linkCopySVG from '../../../icons/link-copy.svg';

const messages = defineMessages({
  label: {
    id: 'share.label',
    defaultMessage: 'Share',
  },
  facebook: {
    id: 'share.facebook',
    defaultMessage: 'Share on Facebook',
  },
  x: {
    id: 'share.x',
    defaultMessage: 'Share on X',
  },
  whatsapp: {
    id: 'share.whatsapp',
    defaultMessage: 'Share on WhatsApp',
  },
  linkedin: {
    id: 'share.linkedin',
    defaultMessage: 'Share on LinkedIn',
  },
  copyLink: {
    id: 'share.copyLink',
    defaultMessage: 'Copy link',
  },
  copied: {
    id: 'share.copied',
    defaultMessage: 'Link copied',
  },
});

const COPIED_TIMEOUT = 2000; // ms

export type ShareButtonsProps = {
  url?: string;
  title?: string;
  className?: string;
};

const getEffectiveUrl = (url?: string) =>
  url || (typeof window !== 'undefined' ? window.location.href : '');

const ShareButtons = ({ url, title = '', className }: ShareButtonsProps) => {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);

  const openShareWindow = (shareUrl: string) => {
    window.open(shareUrl, '_blank', 'noopener,noreferrer');
  };

  const onShareFacebook = () => {
    const shareUrl = getEffectiveUrl(url);
    openShareWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl,
      )}`,
    );
  };

  const onShareX = () => {
    const shareUrl = getEffectiveUrl(url);
    openShareWindow(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl,
      )}&text=${encodeURIComponent(title)}`,
    );
  };

  const onShareWhatsapp = () => {
    const shareUrl = getEffectiveUrl(url);
    openShareWindow(
      `https://wa.me/?text=${encodeURIComponent(
        title,
      )}%20${encodeURIComponent(shareUrl)}`,
    );
  };

  const onShareLinkedin = () => {
    const shareUrl = getEffectiveUrl(url);
    openShareWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        shareUrl,
      )}`,
    );
  };

  const onCopyLink = () => {
    const shareUrl = getEffectiveUrl(url);
    navigator.clipboard?.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), COPIED_TIMEOUT);
  };

  return (
    <div className={cx('share-buttons', className)}>
      <span className="share-buttons__label">
        {intl.formatMessage(messages.label)}
      </span>
      <ul className="share-buttons__list">
        <li>
          <button
            type="button"
            className="share-buttons__button"
            onClick={onShareFacebook}
            aria-label={intl.formatMessage(messages.facebook)}
            title={intl.formatMessage(messages.facebook)}
          >
            <Icon name={facebookSVG} size="18px" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className="share-buttons__button"
            onClick={onShareX}
            aria-label={intl.formatMessage(messages.x)}
            title={intl.formatMessage(messages.x)}
          >
            <Icon name={xTwitterSVG} size="18px" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className="share-buttons__button"
            onClick={onShareWhatsapp}
            aria-label={intl.formatMessage(messages.whatsapp)}
            title={intl.formatMessage(messages.whatsapp)}
          >
            <Icon name={whatsappSVG} size="18px" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className="share-buttons__button"
            onClick={onShareLinkedin}
            aria-label={intl.formatMessage(messages.linkedin)}
            title={intl.formatMessage(messages.linkedin)}
          >
            <Icon name={linkedinSVG} size="18px" />
          </button>
        </li>
        <li>
          <button
            type="button"
            className={cx('share-buttons__button', {
              'share-buttons__button--copied': copied,
            })}
            onClick={onCopyLink}
            aria-label={intl.formatMessage(
              copied ? messages.copied : messages.copyLink,
            )}
            title={intl.formatMessage(
              copied ? messages.copied : messages.copyLink,
            )}
          >
            <Icon name={linkCopySVG} size="18px" />
          </button>
        </li>
      </ul>
    </div>
  );
};

export default ShareButtons;
